/**
 * Renders a duped item the way Minecraft would show it: name, enchantments, lore, durability.
 *
 * This is the point of the whole Dupe Log. "Voided 1x NETHERITE_PICKAXE" tells staff almost nothing;
 * whether it was a plain pickaxe or a Fortune III / Efficiency V / Unbreaking III custom drill with
 * four days left on it is the difference between a shrug and a serious incident.
 *
 * Every string here is player-controlled — item names and lore come out of an anvil — so nothing is
 * ever inserted as HTML. `parseLegacy` returns text segments and React renders them as text nodes.
 */

import { type Segment, enchantmentLabel, parseLegacy, prettyMaterial, toolRemaining } from "../minecraft";

/** Renders legacy-coloured text as styled spans. Text nodes only; no HTML is ever constructed. */
function Coloured({ text, fallbackColor }: { text: string; fallbackColor?: string }): JSX.Element {
  const segments: Segment[] = parseLegacy(text);
  return (
    <>
      {segments.map((segment, index) => (
        <span
          key={index}
          style={{
            color: segment.color ?? fallbackColor,
            fontWeight: segment.bold ? 700 : undefined,
            fontStyle: segment.italic ? "italic" : undefined,
            textDecoration:
              [segment.underline ? "underline" : "", segment.strike ? "line-through" : ""]
                .filter(Boolean)
                .join(" ") || undefined,
            // Static blur rather than an animation: an item name flickering forever in a staff tool
            // is a distraction, and the point is only to show that the flag was set.
            filter: segment.obfuscated ? "blur(3px)" : undefined,
          }}
        >
          {segment.text}
        </span>
      ))}
    </>
  );
}

interface ItemLike {
  material?: string;
  amount?: number;
  display_name?: string;
  lore?: string[];
  unbreakable?: boolean;
  enchantments?: Array<{ key?: string; name?: string; level?: number }>;
  stored_enchantments?: Array<{ key?: string; name?: string; level?: number }>;
  damage?: number;
  max_durability?: number;
  durability_left?: number;
  custom_tool?: {
    type?: string;
    tool_id?: string;
    expires_at?: number;
    expired?: boolean;
    millis_left?: number;
  };
  serialized?: string;
}

export function ItemCard({ item }: { item: Record<string, unknown> }): JSX.Element {
  const data = item as ItemLike;

  const material = typeof data.material === "string" ? data.material : "UNKNOWN";
  const amount = typeof data.amount === "number" ? data.amount : 1;
  const enchantments = Array.isArray(data.enchantments) ? data.enchantments : [];
  const stored = Array.isArray(data.stored_enchantments) ? data.stored_enchantments : [];
  const lore = Array.isArray(data.lore) ? data.lore.filter((l) => typeof l === "string") : [];

  const hasDurability =
    typeof data.max_durability === "number" && data.max_durability > 0;
  const left = typeof data.durability_left === "number" ? data.durability_left : null;
  const durabilityPct =
    hasDurability && left !== null
      ? Math.max(0, Math.min(100, (left / (data.max_durability as number)) * 100))
      : null;

  return (
    <div className="item-card">
      <div className="item-head">
        <div className="item-name">
          {data.display_name ? (
            <Coloured text={data.display_name} />
          ) : (
            prettyMaterial(material)
          )}
        </div>
        <div className="item-material">
          {prettyMaterial(material)}
          {amount > 1 && <span className="item-amount"> &times;{amount}</span>}
          {data.unbreakable === true && <span className="item-flag">Unbreakable</span>}
        </div>
      </div>

      {/* Absence is information here — "no enchantments" is a materially different incident from a
          fully kitted tool, so the section is never hidden. */}
      <div className="item-section">
        <h4>{stored.length > 0 ? "Stored enchantments" : "Enchantments"}</h4>
        {enchantments.length === 0 && stored.length === 0 ? (
          <div className="item-none">No enchantments</div>
        ) : (
          <ul className="ench-list">
            {(stored.length > 0 ? stored : enchantments).map((enchantment, index) => (
              <li key={index}>{enchantmentLabel(enchantment)}</li>
            ))}
          </ul>
        )}
        {/* An enchanted book can carry both: its own enchantments and the ones it grants. */}
        {stored.length > 0 && enchantments.length > 0 && (
          <>
            <h4 style={{ marginTop: 10 }}>Enchantments on the book itself</h4>
            <ul className="ench-list">
              {enchantments.map((enchantment, index) => (
                <li key={index}>{enchantmentLabel(enchantment)}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      {lore.length > 0 && (
        <div className="item-section">
          <h4>Lore</h4>
          <div className="item-lore">
            {lore.map((line, index) => (
              <div key={index}>
                <Coloured text={line} fallbackColor="var(--text-2)" />
              </div>
            ))}
          </div>
        </div>
      )}

      {hasDurability && (
        <div className="item-section">
          <h4>Condition</h4>
          <div className="dura-track">
            <div
              className="dura-fill"
              style={{
                width: `${durabilityPct ?? 0}%`,
                // Minecraft's own durability bar goes green to red as it wears down. Matching it
                // means staff read it without being told what it is.
                background: `hsl(${((durabilityPct ?? 0) / 100) * 120} 70% 45%)`,
              }}
            />
          </div>
          <div className="item-none" style={{ marginTop: 5 }}>
            {left ?? "?"} / {data.max_durability} durability
            {typeof data.damage === "number" && ` · ${data.damage} damage taken`}
          </div>
        </div>
      )}

      {data.custom_tool && (
        <div className="item-section">
          <h4>Custom tool</h4>
          <div className="item-none">
            {data.custom_tool.type ? prettyMaterial(data.custom_tool.type) : "Unknown type"}
            {" · "}
            {/* Recomputed from expires_at now, not the millis_left captured when the row was
                written — that value is stale by however long ago the incident was. */}
            <b style={{ color: data.custom_tool.expired ? "var(--danger)" : "var(--text)" }}>
              {toolRemaining(data.custom_tool.expires_at, data.custom_tool.expired)}
            </b>
          </div>
        </div>
      )}
    </div>
  );
}
